import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { WorkshopModel } from '../../models/workshop.model';
import { RegistrationApprovalModel } from '../../models/registration-approval.model';
import { ProfileService } from '../../../profile/services/profile.service';
import { PersianTitlesService } from '../../../../core/services/persian-titles.service';
import { CommonModule } from '@angular/common';
import { NgIconComponent } from '@ng-icons/core';
import {
  SharedWorkshopCardComponent,
  SharedWorkshopCardAction,
  SharedWorkshopCardRow,
} from 'src/app/shared/components/shared-workshop-card/shared-workshop-card.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from '../../../../shared/shared.module';
import { RegistrationApprovalPromptComponent } from '../../../../shared/components/registration-approval-prompt/registration-approval-prompt.component';
import { DialogModule } from 'primeng/dialog';
@Component({
  selector: 'app-workshop-card',
  templateUrl: './workshop-card.component.html',
  styleUrls: ['./workshop-card.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    SharedWorkshopCardComponent,
    DialogModule,
    TranslateModule,
    RegistrationApprovalPromptComponent,
  ],
})
export class WorkshopCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input('workshop') workshop!: WorkshopModel;
  @Input('canRegister') canRegister: boolean = false;
  @Input('showDescription') showDescription: boolean = false;
  @Input('isRegisteredAndApproved') isRegisteredAndApproved: boolean = false;
  @Input('isRegisteredAndRejected') isRegisteredAndRejected: boolean = false;
  @Input('isRegisteredAndPending') isRegisteredAndPending: boolean = false;
  @Output() onRegister = new EventEmitter<number>();
  subscription: Subscription;
  registrationApprovalProps!: RegistrationApprovalModel;
  showApprovalPromptPopup: boolean = false;

  // Labels
  teacherLabel: string = '';
  holdingTypeLabel: string = '';
  holdingDateLabel: string = '';
  locationLabel: string = '';
  daysLeftLabel: string = '';
  registerLabel: string = '';
  registeredAndApprovedLabel: string = '';
  registeredAndPendingLabel: string = '';
  registeredAndRejectedLabel: string = '';
  addToGoogleCalendarLabel: string = '';

  cardActions: SharedWorkshopCardAction[] = [];
  cardRows: SharedWorkshopCardRow[] = [];

  constructor(
    private profileService: ProfileService,
    private persianTitlesService: PersianTitlesService,
    private translateService: TranslateService,
  ) {}
  ngOnChanges(changes: SimpleChanges): void {
    this.initActions();
  }

  ngOnInit() {
    this.initLabel();
    this.initRows();
    this.initActions();
  }

  onRegisterButtonClick(workshopId: number) {
    this.subscription = this.profileService
      .getMyRegistrationApprovalStatus()
      .subscribe((resp) => {
        this.registrationApprovalProps = resp;

        if (this.registrationApprovalProps.hasApprovalForRegistering == false) {
          this.showApprovalPromptPopup = true;
        } else {
          this.onRegister.emit(workshopId);
        }
      });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  private initLabel() {
    this.teacherLabel = this.persianTitlesService.Items.Teacher;
    this.holdingDateLabel = this.persianTitlesService.Items.HoldingDate;
    this.holdingTypeLabel = this.persianTitlesService.Items.HoldingType;
    this.locationLabel = this.persianTitlesService.Items.Location;
    this.registerLabel = this.persianTitlesService.Items.Register;
    this.registeredAndApprovedLabel =
      this.persianTitlesService.Items.RegisteredAndApproved;
    this.registeredAndPendingLabel =
      this.persianTitlesService.Items.RegisteredAndPending;
    this.registeredAndRejectedLabel =
      this.persianTitlesService.Items.RegisteredAndRejected;
    this.daysLeftLabel = this.persianTitlesService.Items.DaysLeft;
    this.addToGoogleCalendarLabel =
      this.persianTitlesService.Items.addToGoogleCalendar;
  }

  private initRows() {
    this.cardRows = [
      {
        icon: 'lucideUser',
        title: this.teacherLabel,
        value: this.workshop.teacherFullName,
      },
      {
        icon: 'lucideCalendar',
        title: this.holdingDateLabel,
        value: this.workshop.holdingDateJalali,
        badge:
          this.workshop.daysLeft > 0
            ? this.workshop.daysLeft + ' ' + this.daysLeftLabel
            : undefined,
        badgeColor: 'warning',
      },
      {
        icon: 'lucideLayers',
        title: this.holdingTypeLabel,
        value: this.workshop.holdingTypeTitle,
      },
    ];

    if (this.workshop.locationTitle) {
      this.cardRows.push({
        icon: 'lucideMapPin',
        title: this.locationLabel,
        value: this.workshop.locationTitle,
      });
    }

    this.cardRows.push({
      icon: 'lucideUsers',
      title: this.persianTitlesService.Items.RemainingCapacity,
      value:
        (this.workshop.remaining_capacity > 0
          ? this.workshop.remaining_capacity
          : 0) +
        ' ' +
        this.persianTitlesService.Items.Person,
    });
  }

  private initActions() {
    this.cardActions = [
      ...(this.canRegister
        ? [
            {
              type: 'button',
              label: this.registerLabel,
              severity: 'primary',
              icon: 'pi pi-user-plus',
              onClick: () => this.onRegisterButtonClick(this.workshop.id),
            },
          ]
        : []),
      ...(this.isRegisteredAndPending
        ? [
            {
              type: 'button',
              label: this.registeredAndPendingLabel,
              severity: 'primary',
              icon: 'pi pi-hourglass',
              disabled: true,
            },
          ]
        : []),
      ...(this.isRegisteredAndRejected
        ? [
            {
              type: 'button',
              label: this.registeredAndRejectedLabel,
              severity: 'danger',
              icon: 'pi pi-times',
              disabled: true,
            },
          ]
        : []),
      ...(this.isRegisteredAndApproved
        ? [
            {
              type: 'button',
              label: this.registeredAndApprovedLabel,
              severity: 'primary',
              icon: 'pi pi-check',
              disabled: true,
            },
          ]
        : []),
      ...(this.workshop?.googleCalendarNewEventUrl
        ? [
            {
              type: 'link',
              label: this.addToGoogleCalendarLabel,
              outlined: true,
              icon: 'pi pi-calendar',
              link: this.workshop.googleCalendarNewEventUrl,
            },
          ]
        : []),
    ] as SharedWorkshopCardAction[];
  }
}
